import { generateHash } from "../../API/hashUtil";

import { reportsApi } from "../../API/axiosInstance";


/**
 * Maps the current apiSelect value to the corresponding PDF report endpoint.
 */

const getReportEndpoint = (apiSelectValue) => {
  const endpointMap = {
    "api/filter": "/socialmedia",
    "news/report": "/news",
    "api/socialSentiments": "/sentiment",
    "api/images": "/image-analysis",
  };
  return endpointMap[apiSelectValue] || "/socialmedia";
};


/**
 * Maps the current apiSelect value to a user-friendly filename.
 */

const getReportFileName = (apiSelectValue) => {
  const nameMap = {
    "api/filter": "Social_Media_Report",
    "news/report": "News_Report",
    "api/socialSentiments": "Sentiment_Report",
    "analysis/imageData": "Image_Analysis_Report",
  };
  const baseName = nameMap[apiSelectValue] || "Report";
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${baseName}_${timestamp}.pdf`;
};

/**
 * Downloads a PDF report by calling the appropriate report API endpoint.
 *
 * @param {Object} options
 * @param {Object} options.apiSelect - The currently selected report type (e.g. { value: "api/filter", label: "Social Media Report" })
 * @param {string|null} options.reportfromDateTimeFormatted - Start date string
 * @param {string|null} options.reporttoDateTimeFormatted - End date string
 * @param {Array} options.selectedPlatforms - Selected platform filters
 * @param {Array} options.selectedSections - Selected section filters
 * @param {Array} options.selectedLanguages - Selected language filters
 * @param {Array} options.selectedSentiment - Selected sentiment filters
 * @param {Array|null} options.selectedIntensity - Selected intensity filters
 * @param {Array} options.selectedContent - Selected content type filters
 * @param {Array|string|null} options.selectedKeyword - Selected keyword filters
 * @param {Function} [options.onStart] - Callback when download starts
 * @param {Function} [options.onSuccess] - Callback on successful download
 * @param {Function} [options.onError] - Callback on error
 */



export const downloadReportPdf = async ({
  apiSelect,
  reportfromDateTimeFormatted,
  reporttoDateTimeFormatted,
  selectedPlatforms = [],
  selectedSections = [],
  selectedLanguages = [],
  selectedSentiment = [],
  selectedIntensity = [],
  selectedContent = [],
  selectedKeyword = [],
  fileType,
  onStart,
  onSuccess,
  onError,
}) => {


  if (onStart) onStart();

  try {
    // Build filter values
    const platformValues = selectedPlatforms.map((p) => p?.value);
    const platformParams = platformValues.join(",");
    const platformHex = platformValues.join("");

    const sectionValues = selectedSections.map((s) => s?.value);
    const sectionParams = sectionValues.join(",");
    const sectionHex = sectionValues.join("");

    const languageValues = selectedLanguages.map((l) => l?.value);
    const languageParams = languageValues.join(",");
    const languageHex = languageValues.join("");

    const sentimentValues = selectedSentiment.map((s) => s?.value);
    const sentimentParams = sentimentValues.join(",");
    const sentimentHex = sentimentValues.join("");

    const intensityValues =
      selectedIntensity && selectedIntensity.map((i) => i?.value);
    const intensityParams = intensityValues && intensityValues.join(",");
    const intensityHex = intensityValues && intensityValues.join("");

    const contentValues = selectedContent.map((c) => c?.value);
    const contentParams = contentValues.join(",");
    const contentHex = contentValues.join("");

    const keywordsArray = Array.isArray(selectedKeyword)
      ? selectedKeyword
      : selectedKeyword
        ? [selectedKeyword]
        : [];
    const keywordValues = keywordsArray.map((k) => k?.value);
    const keywordParams = keywordValues.join(",");
    const keywordHex = keywordValues.join("");

    // Build hash params based on report type
    const stringParams = {
      startDateStr: reportfromDateTimeFormatted
        ? reportfromDateTimeFormatted.toString()
        : "",
      endDateStr: reporttoDateTimeFormatted
        ? reporttoDateTimeFormatted.toString()
        : "",
      ...(apiSelect.value === "api/socialSentiments" && {
        platforms: platformHex ? platformHex.toString() : "",
        sentiments: sentimentHex ? sentimentHex.toString() : "",
      }),
      ...(apiSelect.value === "api/filter" && {
        platforms: platformHex ? platformHex.toString() : "",
        sections: sectionHex ? sectionHex.toString() : "",
        languages: languageHex ? languageHex.toString() : "",
        sentiments: sentimentHex ? sentimentHex.toString() : "",
        intensity: intensityHex ? intensityHex.toString() : "",
        ctype: contentHex ? contentHex.toString() : "",
        searchKeyword: keywordHex ? keywordHex.toString() : "",
      }),
    };

    const hexHash = generateHash(stringParams);

    const body = {
      startDateStr: reportfromDateTimeFormatted,
      endDateStr: reporttoDateTimeFormatted,
      HPPcode: hexHash,
      filetype: fileType
    };

    // ✅ Send arrays directly (industry standard)
    if (apiSelect.value === "api/filter") {
      if (platformValues.length) body.platforms = platformValues;
      if (sectionValues.length) body.sections = sectionValues;
      if (languageValues.length) body.languages = languageValues;
      if (sentimentValues.length) body.sentiments = sentimentValues;
      if (intensityValues?.length) body.intensity = intensityValues;
      if (contentValues.length) body.ctype = contentValues;
      if (keywordValues.length) body.searchKeywords = keywordValues; // ✅ FIX NAME
    }

    if (apiSelect.value === "api/socialSentiments") {
      if (platformValues.length) body.platforms = platformValues;
      if (sentimentValues.length) body.sentiments = sentimentValues;
    }

    const endpoint = getReportEndpoint(apiSelect.value);

    const mimeTypes = {
      pdf: "application/pdf",
      csv: "text/csv",
      excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };

    const extensions = {
      pdf: ".pdf",
      csv: ".csv",
      excel: ".xlsx",
    };

    const response = await reportsApi.post(`${endpoint}`, body, {
      responseType: "blob",
      headers: {
        "X-XSS-Protection": "1; mode=block",
        "X-Content-Type-Options": "nosniff",
        "X-Frame-Options": "DENY",
        "Referrer-Policy": "strict-origin-when-cross-origin",
        "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
        Accept: mimeTypes[fileType] || "application/pdf",
      },
    });

    const contentType = response.headers["content-type"] || mimeTypes[fileType] || "application/pdf";
    const cd = response.headers["content-disposition"];
    const serverFilename = cd?.match(/filename=([^;]+)/)?.[1]?.replace(/"/g, "");

    const blob = new Blob([response.data], { type: contentType });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = serverFilename || getReportFileName(apiSelect.value).replace(
      /\.[^.]+$/,
      extensions[fileType] || ".pdf"
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    if (onSuccess) onSuccess();
  } catch (error) {
    console.error("Error downloading PDF report:", error);
    const errorMsg =
      error.response?.status === 401
        ? "Session expired. Please log in again."
        : error.response?.status === 404
          ? "Report endpoint not found. Please contact support."
          : "Failed to download PDF report. Please try again.";
    if (onError) onError(new Error(errorMsg));
    else alert(errorMsg);
  }
};
